import { Star } from '@phosphor-icons/react';
import { useId } from 'react';
import { ProgressBar } from 'react-aria-components';
import { Icon } from '../atoms/Icon';
import { cn } from '../lib/cn';
import { uiText } from '../i18n';

export interface StarJarProps {
  stars: number;
  goal: number;
  /** What the stars are collected for, e.g. "Dino-Buch". */
  goalLabel: string;
  size?: 'md' | 'lg';
  className?: string;
}

/**
 * Stars as a glass that fills up – for children who cannot read numbers yet ("picture" profile).
 * Screen readers get the real values through a progress bar.
 */
export function StarJar({ stars, goal, goalLabel, size = 'md', className }: StarJarProps) {
  const clip = useId();
  const ratio = goal > 0 ? Math.min(stars / goal, 1) : 0;
  const fill = 64 * ratio;
  return (
    <ProgressBar
      value={Math.min(stars, goal)}
      maxValue={goal}
      aria-label={uiText().jar(goalLabel)}
      valueLabel={uiText().jarValue(String(stars), String(goal))}
      className={cn('flex items-center gap-gap-lg', className)}
    >
      <svg
        viewBox="0 0 76 92"
        aria-hidden
        className={cn(
          'shrink-0',
          size === 'md' ? 'h-avatar-lg w-avatar-md' : 'h-avatar-xl w-avatar-lg',
        )}
      >
        <defs>
          <clipPath id={clip}>
            <rect x="10" y="20" width="56" height="66" rx="12" />
          </clipPath>
        </defs>
        <rect x="18" y="6" width="40" height="12" rx="4" className="fill-text-muted" />
        <rect
          x="8"
          y="18"
          width="60"
          height="70"
          rx="14"
          strokeWidth="3"
          className="fill-reward-points-surface stroke-text-primary"
        />
        <g clipPath={`url(#${clip})`}>
          <rect
            x="8"
            y={86 - fill}
            width="60"
            height={fill + 2}
            className="fill-reward-points transition-all duration-(--shh-motion-duration-slow) ease-bounce"
          />
        </g>
      </svg>
      <span className="flex flex-col gap-gap-xs">
        <span className="flex items-center gap-gap-xs font-display text-3xl font-black tabular-nums leading-none">
          <Icon icon={Star} weight="fill" size="lg" className="text-reward-points-ink" />
          {stars}
        </span>
        <span className="text-md text-text-secondary">
          {stars >= goal ? (
            <>
              Geschafft! <strong className="text-text-primary">{goalLabel}</strong>
            </>
          ) : (
            <>
              noch <strong className="text-text-primary">{goal - stars}</strong> bis{' '}
              <strong className="text-text-primary">{goalLabel}</strong>
            </>
          )}
        </span>
      </span>
    </ProgressBar>
  );
}
